import { parse } from "query-string";
import React from "react";

import BlockedBazaarMessage from "@/mk2/apps/bazaar/components/BlockedBazaarMessage";
import FilterLinks from "@/mk2/apps/bazaar/components/FilterLinks";
import { MessageFormData } from "@/mk2/apps/bazaar/components/MessageForm";
import Navigation from "@/mk2/apps/bazaar/components/Navigation";
import ProductBuyers from "@/mk2/apps/bazaar/components/ProductBuyers";
import ProductInfo from "@/mk2/apps/bazaar/components/ProductInfo";
import ProductPhoto from "@/mk2/apps/bazaar/components/ProductPhoto";
import ProductReport from "@/mk2/apps/bazaar/components/ProductReport";
import { ProductState } from "@/mk2/apps/bazaar/components/ProductState";
import RelatedProducts, {
    RelatedProductsPlaceholder,
} from "@/mk2/apps/bazaar/components/RelatedProducts";
import { ReviewFormData } from "@/mk2/apps/bazaar/components/ReviewForm";
import SecondaryImages from "@/mk2/apps/bazaar/components/SecondaryImages";
import SellerActionButtons from "@/mk2/apps/bazaar/components/SellerActionButtons";
import { SellerInfo } from "@/mk2/apps/bazaar/components/SellerInfo";
import { SellerShowPhone } from "@/mk2/apps/bazaar/components/SellerShowPhone";
import SellerStatusMessage from "@/mk2/apps/bazaar/components/SellerStatusMessage";
import IsSoldPoll from "@/mk2/apps/bazaar/containers/IsSoldPoll/IsSoldPoll";
import { ContactSeller } from "@/mk2/apps/bazaar/containers/ProductDetail/ContactSeller";
import { Props } from "@/mk2/apps/bazaar/containers/ProductDetail/ProductDetailPage";
import { paramsToSlug, slugToParams } from "@/mk2/apps/bazaar/helpers";
import { DeletedReason } from "@/mk2/apps/bazaar/schemas";
import { bazaarProductDetailUrl, bazaarUrl } from "@/mk2/apps/bazaar/urls";
import { phoneVerificationUrl } from "@/mk2/apps/users/urls";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { Failure } from "@/mk2/components/errors/Failure";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import AdSlotOffsite from "@/mk2/containers/AdSlotOffsite/AdSlotOffsite";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import Loadable from "@/mk2/helpers/Loadable";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isLoading } from "@/mk2/reducers/loadingState";
import { redirectInPWA } from "@/mk2/services/browserHistory";
import {
    BACK_TO_BAZAAR_GRID,
    BZ2_PRODUCT_HAS_COMPLAINTS,
    BZ2_REMOVE_COMPLAINTS,
    BZ2_REPORT_MARKS_CHECKED_BY_usernames,
    PRODUCT_DETAIL_COMPLAINT_HINT,
    THE_ENTRY_DOES_NOT_EXIST,
} from "@/mk/autogenerated/translation";
import {
    canManageBazaar,
    canReportProducts,
    isAuthenticated,
    isAuthenticatedAs,
} from "@/mk/bazaar/common/userUtils";

import styles from "./ProductDetail.mscss";

const StrollerInfo = Loadable({
    loader: () =>
        import(
            "@/mk2/apps/bazaar/components/StrollerInfo" /* webpackChunkName: "bazaar.components.StrollerInfo" */
        ).then((mod) => mod.StrollerInfo),
    modules: ["@/mk2/apps/bazaar/components/StrollerInfo"],
    webpack: () => [require.resolveWeak("@/mk2/apps/bazaar/components/StrollerInfo")],
});

const VIEW_TIMEOUT = 50; // 50ms

interface State {
    collapsedImages: boolean;
}

const complaintHintComponents = [<Link key="link" to="/help/#cat-howto-market-cheat-buyer" />];

class ProductDetail extends React.Component<Props, State> {
    public state = {
        collapsedImages: true,
    };

    private buyersRef = React.createRef<HTMLDivElement>();

    private viewTimer: number;

    public componentDidMount(): void {
        this.viewTimer = window.setTimeout(this.onView, VIEW_TIMEOUT);

        if (this.props.index >= 0 && this.props.product?.id) {
            this.props.onNavigationLoad(this.props.product.id, this.props.location);
        }
    }

    public componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>,
        snapshot?: any,
    ): void {
        if (prevProps.product?.id !== this.props.product?.id) {
            this.setState({
                collapsedImages: true,
            });

            // Refresh product transactions after 5 minutes
            if (this.props.loadedAt && this.props.loadedAt < Date.now() - 5 * 60 * 1000) {
                // Refresh transactions when product has changed
                this.onLoad();
            }

            if (this.props.index >= 0 && this.props.product?.id) {
                this.props.onNavigationLoad(this.props.product.id, this.props.location);
            }

            clearTimeout(this.viewTimer);
            this.viewTimer = window.setTimeout(this.onView, VIEW_TIMEOUT);
        }
    }

    public componentWillUnmount(): void {
        clearTimeout(this.viewTimer);
    }

    public render() {
        const {
            loadingState,
            product,
            back,
            routeNavigate,
            hits,
            index,
            nextProduct,
            previousProduct,
        } = this.props;
        return (
            <div className={styles.ProductDetail}>
                {index >= 0 && (
                    <Navigation
                        className={styles.ProductDetail__navigation}
                        routeNavigate={routeNavigate}
                        back={back}
                        hits={hits}
                        index={index}
                        nextProduct={nextProduct}
                        previousProduct={previousProduct}
                    />
                )}

                <div className={styles.ProductDetail__content}>
                    <LoadingSwitch
                        loadingState={loadingState}
                        onLoad={this.onLoad}
                        onRenderSuccess={this.renderSuccess}
                        onRenderInit={this.renderInit}
                        onRenderFailure={this.renderFailure}
                        hasData={Boolean(product)}
                        onFailureToast={this.onFailureToast}
                    />
                </div>
            </div>
        );
    }

    private renderInit = () => {
        // TODO: Implement
        return null;
    };

    private renderFailure = () => {
        const {
            location,
            errorMessage,
            product,
            relatedProducts,
            relatedProductsHasNext,
            relatedProductsLoadingState,
            isMobile,
        } = this.props;

        if (errorMessage.toUpperCase() === "GONE") {
            return (
                <>
                    <div className={styles.ProductDetail__gone}>
                        <strong className={styles.ProductDetail__gone__title}>
                            {THE_ENTRY_DOES_NOT_EXIST}
                        </strong>
                    </div>
                    <RelatedProducts
                        className={styles.ProductDetail__relatedProducts}
                        relatedProducts={relatedProducts}
                        product={product}
                        loadingState={relatedProductsLoadingState}
                        hasNext={relatedProductsHasNext}
                        isMobile={isMobile}
                        location={location}
                        onLoad={this.onLoadRelatedProducts}
                    />
                </>
            );
        }

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.onLoad}
                backButtonUrl={bazaarUrl}
                backButtonLabel={BACK_TO_BAZAAR_GRID}
            />
        );
    };

    private renderSuccess = () => {
        const {
            requestUser,
            requestUserPermissions,
            marks,
            complaints,
            product,
            buyers,
            isMobile,
            index,
            location,
            closedBuyers,
            messages,
            reviews,
            relatedProducts,
            relatedProductsHasNext,
            relatedProductsLoadingState,
            lastNote,
            loadingState,
            activatingProductIds,
            deactivatingProductIds,
            deletingProductIds,
            undeletingProductIds,
            repostingProductIds,
            nextProduct,
            markingOKProductIds,
            sellerPhone,
            stroller,
            strollerReviewedBySeller,
            viewsCount,
            historyCount,
            onNewMessage,
        } = this.props;

        const isAuth = isAuthenticated(requestUser);
        const isModerator = canReportProducts(requestUserPermissions);
        const isAdmin = canManageBazaar(requestUserPermissions);
        const isSeller =
            product.seller?.user && isAuthenticatedAs(requestUser, product.seller.user);

        const isVisible =
            product.state.isActive ||
            // Expired product should be visible for 30 days
            // after 30 days server API will always return 410 Gone, so we will never reach this code
            product.state.isExpired ||
            // or there are some transactions with this product
            // TODO: Check if there are some transactions
            buyers.length > 0 ||
            // or I'm seller
            isSeller ||
            // or Moderator which just deleted the product
            isModerator ||
            // or I can manage products
            isAdmin;

        const qs = parse(location.search);
        const contactFormInitiallyOpen = qs.openContactForm === "1";
        const showSellerPhone = product.seller?.showSellerPhone;

        return (
            <>
                {(isModerator || isAdmin) && marks.length > 0 && (
                    <div className={styles.ProductDetail__marks}>
                        <Interpolate
                            i18nKey={BZ2_REPORT_MARKS_CHECKED_BY_usernames}
                            usernames={
                                <strong>{marks.map((m) => m.admin.username).join(", ")}</strong>
                            }
                            count={marks.length}
                        />
                    </div>
                )}
                {(isModerator || isAdmin) && complaints.length > 0 && !product.state.isDeleted && (
                    <Complaints
                        className={styles.ProductDetail__complaints}
                        id={product.id}
                        entity={ComplaintTargetType.BazaarProduct}
                        deleteLabel={BZ2_REMOVE_COMPLAINTS}
                        header={
                            <h4 className={styles.ProductDetail__complaints__title}>
                                {BZ2_PRODUCT_HAS_COMPLAINTS + " "}
                                <span className={styles.ProductDetail__complaints__count}>
                                    ({complaints.length}x)
                                </span>
                            </h4>
                        }
                    />
                )}

                {product.seller?.hasBlockedBazaar && (
                    <BlockedBazaarMessage
                        className={styles.ProductDetail__blockedMessage}
                        isSeller={isSeller}
                        selfBlocked={product.seller?.user?.isBlockedSelf}
                        username={product.seller?.username}
                    />
                )}

                <ProductState
                    className={styles.ProductDetail__state}
                    product={product}
                    isSeller={isSeller}
                    isAdmin={isAdmin}
                    onDelete={this.onDelete}
                    onRepost={this.onRepost}
                    onActivate={this.onActivate}
                    isActivating={activatingProductIds.includes(product.id)}
                    isDeleting={deletingProductIds.includes(product.id)}
                    isReposting={repostingProductIds.includes(product.id)}
                />

                {isVisible && (
                    <>
                        <Complaint
                            id={product.id}
                            entity={ComplaintTargetType.BazaarProduct}
                            className={styles.ProductDetail__complaint}
                            scrollOffset={44 + 44 + 30}
                            inForum={false}
                            inGroups={false}
                            hint={
                                <Interpolate
                                    i18nKey={PRODUCT_DETAIL_COMPLAINT_HINT}
                                    components={complaintHintComponents}
                                />
                            }
                        />
                        <div className={styles.ProductDetail__data}>
                            <div className={styles.ProductDetail__data__first}>
                                {isMobile && isAuth && !isSeller && (
                                    <ProductReport
                                        isActive={product.state.isActive}
                                        isDeleted={product.state.isDeleted}
                                        isReported={complaints.some((c) =>
                                            isAuthenticatedAs(requestUser, c.user),
                                        )}
                                        isModerator={isModerator || isAdmin}
                                        hasNavigation={index >= 0}
                                        onReport={this.onReport}
                                        onMarkOk={this.onMarkOk}
                                        onMarkOkAndNext={this.onMarkOkAndNext}
                                        isMarkingOK={markingOKProductIds.includes(product.id)}
                                        isMarkedOK={marks.some((m) =>
                                            isAuthenticatedAs(requestUser, m.admin),
                                        )}
                                    />
                                )}
                                {isMobile && product.photos?.[0]?.photofile && (
                                    <ProductPhoto
                                        className={styles.ProductDetail__cover}
                                        photo={product.photos[0].photofile}
                                        alt={product.seo.name}
                                        maxHeight={380}
                                    />
                                )}
                                <ProductInfo
                                    product={product}
                                    viewsCount={viewsCount}
                                    adSlot={
                                        isMobile && !product.isNoads ? (
                                            <AdSlot
                                                zone="Market Item - Top 01 - Mobile"
                                                location={location}
                                                marginTopWithAd={10}
                                                marginBottomWithAd={10}
                                            />
                                        ) : undefined
                                    }
                                />
                                {stroller && (
                                    <StrollerInfo
                                        stroller={stroller}
                                        strollerReviewedBySeller={strollerReviewedBySeller}
                                        isSeller={isSeller}
                                        product={product}
                                        className={styles.ProductDetail__stroller}
                                    />
                                )}
                                {product.seller && (
                                    <Panel className={styles.ProductDetail__sellerAdditionalInfo}>
                                        <SellerStatusMessage
                                            className={styles.ProductDetail__sellerStatusMessage}
                                            seller={product.seller}
                                            hidePanel
                                            hideSeller
                                        />
                                        <ContactSeller
                                            className={
                                                showSellerPhone && styles.ProductDetail__contact
                                            }
                                            product={product}
                                            buyers={buyers}
                                            messages={messages}
                                            sellerPhone={sellerPhone}
                                            requestUser={requestUser}
                                            requestUserPermissions={requestUserPermissions}
                                            initiallyOpened={contactFormInitiallyOpen}
                                            isLoadingProductDetails={isLoading(loadingState)}
                                            onSendMessage={onNewMessage}
                                            onScrollToMessages={this.onSentAnother}
                                        />
                                        {isSeller && (
                                            <SellerShowPhone
                                                seller={product.seller}
                                                requestUser={requestUser}
                                                sellerPhone={sellerPhone}
                                                onSetShowSellerPhone={this.onSetShowSellerPhone}
                                            />
                                        )}
                                    </Panel>
                                )}
                                {(isAdmin || isModerator || isSeller) && (
                                    <SellerActionButtons
                                        className={styles.ProductDetail__sellerActionButtons}
                                        product={product}
                                        isSeller={isSeller}
                                        isAdmin={isAdmin}
                                        isModerator={isModerator}
                                        lastNote={lastNote}
                                        historyCount={historyCount}
                                        onActivate={this.onActivate}
                                        onDeactivate={this.onDeactivate}
                                        onDelete={this.onDelete}
                                        onUndelete={this.onUndelete}
                                        onRepost={this.onRepost}
                                        isActivating={activatingProductIds.includes(product.id)}
                                        isDeactivating={deactivatingProductIds.includes(product.id)}
                                        isDeleting={deletingProductIds.includes(product.id)}
                                        isUndeleting={undeletingProductIds.includes(product.id)}
                                        isReposting={repostingProductIds.includes(product.id)}
                                    />
                                )}
                                {!isSeller && product.seller && (
                                    <SellerInfo
                                        className={styles.ProductDetail__sellerInfo}
                                        seller={product.seller}
                                    />
                                )}
                                {!product.isNoads && !isMobile && (
                                    <div className={styles.ProductDetail__rectangleAdGrow}>
                                        <div className={styles.ProductDetail__rectangleAdSticky}>
                                            <AdSlot
                                                key={product.id}
                                                className={styles.ProductDetail__rectangleAd}
                                                zone="Market Item - Mid 01 - Desktop"
                                                location={location}
                                                marginTopWithAd={20}
                                            />
                                        </div>
                                    </div>
                                )}
                                {!product.isNoads && isMobile && (
                                    <AdSlot
                                        className={styles.ProductDetail__rectangleAd}
                                        key={product.id}
                                        zone="Market Item - Mid 01 - Mobile"
                                        location={location}
                                        marginTopWithAd={20}
                                    />
                                )}
                            </div>
                            <div className={styles.ProductDetail__data__second}>
                                {!isMobile && isAuth && !isSeller && (
                                    <ProductReport
                                        isActive={product.state.isActive}
                                        isDeleted={product.state.isDeleted}
                                        isReported={complaints.some((c) =>
                                            isAuthenticatedAs(requestUser, c.user),
                                        )}
                                        isModerator={isModerator || isAdmin}
                                        hasNavigation={Boolean(nextProduct)}
                                        onReport={this.onReport}
                                        onMarkOk={this.onMarkOk}
                                        onMarkOkAndNext={this.onMarkOkAndNext}
                                        isMarkingOK={markingOKProductIds.includes(product.id)}
                                        isMarkedOK={marks.some((m) =>
                                            isAuthenticatedAs(requestUser, m.admin),
                                        )}
                                    />
                                )}
                                {!isMobile && product.photos?.[0]?.photofile && (
                                    <ProductPhoto
                                        className={styles.ProductDetail__cover}
                                        photo={product.photos[0].photofile}
                                        alt={product.seo.name}
                                    />
                                )}
                                {product.photos.filter(Boolean).length > 0 && (
                                    <SecondaryImages
                                        className={styles.ProductDetail__secondaryImages}
                                        collapsed={this.state.collapsedImages}
                                        collapsible={
                                            // Remove when product.images will be removed
                                            (product.photos.length || 1) - 1 > 0 &&
                                            buyers.length > 0
                                        }
                                        images={product.photos.slice(1).map((p) => p.photofile)}
                                        alt={product.seo.name}
                                        onToggle={this.onToggleSecondaryImages}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}

                {buyers.length + closedBuyers.length > 0 && (
                    <div ref={this.buyersRef}>
                        <ProductBuyers
                            className={styles.ProductDetail__buyers}
                            buyers={buyers}
                            closedBuyers={closedBuyers}
                            messages={messages}
                            reviews={reviews}
                            requestUser={requestUser}
                            requestUserPermissions={requestUserPermissions}
                            onRead={this.onRead}
                            onNewReview={this.onNewReview}
                            onNewMessage={this.onMessageReply}
                            onRemoveBuyer={this.onRemoveBuyer}
                            onRestoreBuyer={this.onRestoreBuyer}
                        />
                    </div>
                )}
                {!product.isNoads && (
                    <AdSlot
                        key={product.id}
                        className={styles.ProductDetail__rectangleAd}
                        zone="Market Item - Mid 02 - Mobile"
                        location={location}
                        marginTopWithAd={20}
                    />
                )}
                {isVisible && (
                    <FilterLinks className={styles.ProductDetail__filterLinks} product={product} />
                )}
                {isLoading(loadingState) ? (
                    <RelatedProductsPlaceholder className={styles.ProductDetail__relatedProducts} />
                ) : (
                    <RelatedProducts
                        className={styles.ProductDetail__relatedProducts}
                        relatedProducts={relatedProducts}
                        product={product}
                        loadingState={relatedProductsLoadingState}
                        hasNext={relatedProductsHasNext}
                        isMobile={isMobile}
                        location={location}
                        onLoad={this.onLoadRelatedProducts}
                    />
                )}

                {!isMobile && !product.isNoads && (
                    <AdSlotOffsite
                        zone="Market Item - Top 02 - Desktop"
                        requestUser={requestUser}
                        location={location}
                    />
                )}

                {isSeller && <IsSoldPoll />}
            </>
        );
    };

    private onLoad = () => {
        const { onLoad, routeParams } = this.props;
        onLoad(paramsToSlug(routeParams));
    };

    private onLoadRelatedProducts = (start: number) => {
        const { onLoadRelatedProducts, routeParams, relatedProductsLoadingState } = this.props;

        if (relatedProductsLoadingState !== LoadingState.LOADING) {
            onLoadRelatedProducts(paramsToSlug(routeParams), start);
        }
    };

    private onActivate = () => {
        const { onActivate, product } = this.props;
        onActivate(product.id);
    };

    private onDeactivate = () => {
        const { onDeactivate, product } = this.props;
        onDeactivate(product.id);
    };

    private onDelete = (reason?: DeletedReason) => {
        const { onDelete, product } = this.props;
        onDelete(product.id, reason);
    };

    private onUndelete = () => {
        const { onUndelete, product } = this.props;
        onUndelete(product.id);
    };

    private onRepost = () => {
        const { onRepost, product } = this.props;
        onRepost(product.id);
    };

    private onReport = () => {
        const { onReport, product, location } = this.props;
        onReport(product.id, location);
    };

    private onMarkOk = () => {
        const { onMarkOK, product } = this.props;
        onMarkOK(product.id);
    };

    private onMarkOkAndNext = () => {
        const { onMarkOK, product, routeNavigate, nextProduct } = this.props;
        if (nextProduct) {
            routeNavigate(url(bazaarProductDetailUrl, slugToParams(nextProduct.slug)));
        }
        onMarkOK(product.id);
    };

    private onSentAnother = () => {
        if (this.buyersRef.current) {
            this.buyersRef.current.scrollIntoView(true);
        }
    };

    private onRead = (messagesIds: number[]) => {
        const { onReadMessages } = this.props;
        onReadMessages(messagesIds);
    };

    private onNewReview = (buyerId: number, data: ReviewFormData) => {
        const { onNewReview, product } = this.props;
        onNewReview(buyerId, product.slug, data);
    };

    private onMessageReply = (buyerId: number, data: MessageFormData) => {
        const { onNewReply, product } = this.props;
        onNewReply(buyerId, product.slug, data);
    };

    private onRemoveBuyer = (buyerId: number) => {
        const { onRemoveBuyer, product } = this.props;
        onRemoveBuyer(product.slug, buyerId);
    };

    private onRestoreBuyer = (buyerId: number) => {
        const { onRestoreBuyer, product } = this.props;
        onRestoreBuyer(product.slug, buyerId);
    };

    private onToggleSecondaryImages = () => {
        this.setState((s) => ({
            collapsedImages: !s.collapsedImages,
        }));
    };

    private onView = () => {
        const { onView, product, requestUser, errorMessage } = this.props;
        // Ignore when product not loaded or loaded with error or user is seller
        if (
            !product ||
            errorMessage ||
            (product.seller?.user && isAuthenticatedAs(requestUser, product.seller.user))
        ) {
            return;
        }

        onView(product.id, product.slug);
    };

    private onFailureToast = () => {
        const { errorMessage, onFailureToast } = this.props;
        if (!["GONE", "NOT FOUND"].includes(errorMessage.toUpperCase())) {
            onFailureToast();
        }
    };

    private onSetShowSellerPhone = (value: boolean) => {
        const { onSetShowSellerPhone, product, requestUser, routeNavigate } = this.props;
        if (!requestUser.phoneVerified) {
            const gotoUrl = url(
                phoneVerificationUrl,
                {},
                { next: `${location.pathname}${location.search}` },
            );
            redirectInPWA(routeNavigate, gotoUrl);
        } else {
            onSetShowSellerPhone(value, product.slug, requestUser.phoneVerified, requestUser.id);
        }
    };
}

export default ProductDetail;
